// Clients App → Pop-upy (17.7.2026) — přehled všech pop-upů klientské
// aplikace: aktivní, plánované, ukončené i systémové, s pravidly zobrazování
// a konverzemi (CTA kliknutí z client_events). Owner only.
//
// Pop-upy žijí v kódu portálu — tahle stránka je katalog. Chceš minulý popup
// znovu spustit nebo upravit? Změna je v portálu (konstanty období/frekvence).
import React, { useEffect, useState } from "react";
import { Megaphone, Loader2, RefreshCw } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { apiFetch } from "../../lib/api.js";

const STAV = {
  aktivni: { label: "Aktivní", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  planovany: { label: "Plánovaný", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  ukonceny: { label: "Ukončený", cls: "bg-slate-200 text-slate-600 border-slate-300" },
  systemovy: { label: "Systémový", cls: "bg-plum-100 text-plum-800 border-plum-200" },
};

const fmtDatum = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${parseInt(d, 10)}. ${parseInt(m, 10)}. ${y}`;
};

function PopupKarta({ p }) {
  const st = STAV[p.stav] || STAV.aktivni;
  const obdobi = p.od || p.do
    ? `${fmtDatum(p.od) || "…"} – ${fmtDatum(p.do) || "bez konce"}`
    : "Bez časového omezení";
  return (
    <div className={`bg-white border rounded-2xl p-4 shadow-sm space-y-2.5 ${p.stav === "ukonceny" ? "opacity-75 border-slate-200" : "border-slate-200"}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="text-base font-bold text-slate-900 flex items-center gap-2">
          <span className="text-xl">{p.emoji}</span> {p.nazev}
        </div>
        <span className={`text-xs font-semibold px-2 py-1 rounded-lg border ${st.cls}`}>{st.label}</span>
      </div>
      <div className="text-sm text-slate-600">{p.popis}</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
        <div className="bg-slate-50 border border-slate-100 rounded-xl px-2.5 py-2">
          <div className="font-semibold text-slate-500 uppercase tracking-wide text-[11px]">Období</div>
          <div className="text-slate-800 mt-0.5">{obdobi}</div>
        </div>
        <div className="bg-slate-50 border border-slate-100 rounded-xl px-2.5 py-2">
          <div className="font-semibold text-slate-500 uppercase tracking-wide text-[11px]">Frekvence</div>
          <div className="text-slate-800 mt-0.5">{p.frekvence}</div>
        </div>
        <div className="bg-slate-50 border border-slate-100 rounded-xl px-2.5 py-2">
          <div className="font-semibold text-slate-500 uppercase tracking-wide text-[11px]">Cílení</div>
          <div className="text-slate-800 mt-0.5">{p.cileni}</div>
        </div>
      </div>
      {p.konverze && (
        <div className="text-sm bg-brand-50 border border-brand-100 rounded-xl px-3 py-2 text-brand-900">
          <span className="font-bold">{p.konverze.kliknuti}×</span> kliknutí na CTA od <span className="font-bold">{p.konverze.klientek}</span> klientek
          {p.konverze.posledni && <span className="text-brand-500">, naposledy {fmtDatum(p.konverze.posledni)}</span>}
        </div>
      )}
      {p.pozn && <div className="text-xs text-slate-400">{p.pozn}</div>}
    </div>
  );
}

export default function ClientsAppPopupy() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const nacti = () => {
    setLoading(true); setErr(null);
    apiFetch("/api/popupy-prehled")
      .then((r) => r.json())
      .then((j) => { if (j.ok) setData(j); else setErr(j.error || "Chyba"); })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(nacti, []);

  if (loading && !data) return <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (err) return <div className="p-6 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl">{err}</div>;

  const popupy = data?.popupy || [];
  const rad = { aktivni: 0, planovany: 1, systemovy: 2, ukonceny: 3 };
  const serazene = [...popupy].sort((a, b) => (rad[a.stav] ?? 9) - (rad[b.stav] ?? 9));

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-4xl">
      <GradientHeader
        title="Pop-upy v aplikaci"
        subtitle="Aktivní kampaně, systémové zprávy i ukončené akce. Klient vidí vždy nejvýš jeden pop-up na otevření a každý má vlastní pravidla, aby nespamoval."
        icon={Megaphone}
        loading={loading}
        akce={[{ klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: nacti, disabled: loading }]}
      />
      <div className="space-y-3">
        {serazene.map((p) => <PopupKarta key={p.klic} p={p} />)}
      </div>
    </div>
  );
}
